import { TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNumericInput } from '../../../shared/hooks/use-numeric-input';
import { getTemperatureInputStyles } from './styles';

interface TemperatureInputProps {
  temperature: number | undefined;
  validation: { isCompliant: boolean; message: string } | null | undefined;
  hasValue: boolean;
  isCompliant: boolean | null;
  onChange: (value: number | undefined) => void;
}

/**
 * Formatta un numero per la visualizzazione iniziale (max 2 decimali, senza zeri trailing).
 * Usato solo per inizializzare lo stato locale, MAI durante la digitazione.
 */
function formatForDisplay(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return rounded.toString();
}

/**
 * Valida se una stringa rappresenta un input di temperatura valido (anche parziale).
 * Permette: numeri, numeri negativi, decimali incompleti come "5." o "-3."
 */
function isValidTemperatureInput(value: string): boolean {
  if (value === '' || value === '-' || value === '.' || value === '-.') {
    return true;
  }
  // Pattern: opzionale segno negativo, cifre, opzionale punto decimale con cifre
  return /^-?\d*\.?\d*$/.test(value);
}

/**
 * Parsa una stringa in un numero, restituendo undefined se non è un numero valido completo.
 */
function parseTemperature(value: string): number | undefined {
  if (value === '' || value === '-' || value === '.' || value === '-.') {
    return undefined;
  }
  // Rifiuta stringhe che terminano con un punto (input incompleto)
  if (value.endsWith('.')) {
    return undefined;
  }
  const parsed = Number.parseFloat(value);
  return Number.isNaN(parsed) ? undefined : parsed;
}

export function TemperatureInput({
  temperature,
  validation,
  hasValue,
  isCompliant,
  onChange,
}: TemperatureInputProps) {
  const { t } = useTranslation('temperature');
  const { inputValue, handleChange, handleBlur } = useNumericInput({
    value: temperature,
    onChange,
    isValidInput: isValidTemperatureInput,
    parse: parseTemperature,
    format: formatForDisplay,
  });

  const helperText = hasValue && validation ? validation.message : t('helperDefault');

  return (
    <TextField
      label={t('temperatureLabel')}
      type="text"
      inputMode="decimal"
      value={inputValue}
      onChange={handleChange}
      onBlur={handleBlur}
      error={hasValue && isCompliant === false}
      helperText={helperText}
      slotProps={{
        formHelperText: {
          sx: {
            textAlign: 'center',
            fontSize: { xs: '0.75rem', sm: '0.8125rem' },
            m: { xs: '4px 0 0', sm: '4px 0 0' },
            color:
              hasValue && isCompliant === false
                ? 'error.main'
                : hasValue && isCompliant === true
                  ? 'success.main'
                  : 'text.secondary',
          },
        },
      }}
      sx={getTemperatureInputStyles()}
      size="medium"
    />
  );
}
